// components/RepliesPage/ReplyTabs.tsx
import {
  CheckCircle,
  Clock,
  FilterX,
  ShieldAlert,
  LoaderCircle,
  RefreshCw,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

// Import tab content components
import RepliedTab from "./tabs/RepliedTab";
import PendingTab from "./tabs/PendingTab";
import SkippedTab from "./tabs/SkippedTab";
import SpamTab from "./tabs/SpamTab";

interface ReplyTabsProps {
  activeTab: string;
  onTabChange: (tab: string) => void;
  sortBy: string;
  onSortChange: (sort: string) => void;
  isRefreshing: boolean;
  isRepliesLoading: boolean;
  onRefresh: () => Promise<void>;
  sortedReplies: any[];
  isRepliedMessagesLoading: boolean;
  isPendingRepliesLoading: boolean;
  isSkippedRepliesLoading: boolean;
  personaId: string;
  personaData: any;
  automatedReplies: boolean;
  handleApprove: () => void;
  handleReject: () => void;
}

const ReplyTabs: React.FC<ReplyTabsProps> = ({
  activeTab,
  onTabChange,
  sortBy,
  onSortChange,
  isRefreshing,
  isRepliesLoading,
  onRefresh,
  sortedReplies,
  isRepliedMessagesLoading,
  isPendingRepliesLoading,
  isSkippedRepliesLoading,
  personaId,
  personaData,
  automatedReplies,
  handleApprove,
  handleReject,
}) => {
  return (
    <Tabs
      defaultValue="replied"
      value={activeTab}
      onValueChange={onTabChange}
      className="w-full"
    >
      <div className="flex flex-col space-y-3">
        <div className="flex justify-end items-center gap-2 mb-1">
          <Button
            variant="outline"
            className="h-10"
            onClick={onRefresh}
            disabled={isRepliesLoading || isRefreshing}
          >
            {isRefreshing ? (
              <LoaderCircle className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <RefreshCw className="h-4 w-4 mr-2" />
            )}
            REFRESH
          </Button>

          <Select
            value={sortBy}
            onValueChange={onSortChange}
            disabled={isRepliesLoading}
          >
            <SelectTrigger className="h-10 min-w-[140px]">
              <SelectValue placeholder="Sort by" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="newest">Newest First</SelectItem>
              <SelectItem value="oldest">Oldest First</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Tabs below */}
        <TabsList className="w-full grid grid-cols-4">
          <TabsTrigger
            value="replied"
            className="gap-2 min-w-0 px-1 sm:px-3 relative"
          >
            <CheckCircle className="h-4 w-4 min-w-4 min-h-4 flex-shrink-0" />
            <span className="hidden sm:inline">Replied</span>
            {sortedReplies.length > 0 && activeTab === "replied" && (
              <span className="absolute -top-1 -right-1 text-xs bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-white px-1.5 py-0.5 rounded-full min-w-[1.2rem] text-center">
                {sortedReplies.length}
              </span>
            )}
          </TabsTrigger>
          <TabsTrigger
            value="pending"
            className="gap-2 min-w-0 px-1 sm:px-3 relative"
          >
            <Clock className="h-4 w-4 min-w-4 min-h-4 flex-shrink-0" />
            <span className="hidden sm:inline">Pending</span>
            {sortedReplies.length > 0 && activeTab === "pending" && (
              <span className="absolute -top-1 -right-1 text-xs bg-amber-200 dark:bg-amber-800 text-amber-800 dark:text-amber-100 px-1.5 py-0.5 rounded-full min-w-[1.2rem] text-center">
                {sortedReplies.length}
              </span>
            )}
          </TabsTrigger>
          <TabsTrigger
            value="skipped"
            className="gap-2 min-w-0 px-1 sm:px-3 relative"
          >
            <FilterX className="h-4 w-4 min-w-4 min-h-4 flex-shrink-0" />
            <span className="hidden sm:inline">Skipped</span>
            {sortedReplies.length > 0 && activeTab === "skipped" && (
              <span className="absolute -top-1 -right-1 text-xs bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-white px-1.5 py-0.5 rounded-full min-w-[1.2rem] text-center">
                {sortedReplies.length}
              </span>
            )}
          </TabsTrigger>
          <TabsTrigger
            value="spam"
            className="gap-2 min-w-0 px-1 sm:px-3 relative"
          >
            <ShieldAlert className="h-4 w-4 min-w-4 min-h-4 flex-shrink-0" />
            <span className="hidden sm:inline">Spam</span>
            {sortedReplies.length > 0 && activeTab === "spam" && (
              <span className="absolute -top-1 -right-1 text-xs bg-red-200 dark:bg-red-800 text-red-800 dark:text-red-100 px-1.5 py-0.5 rounded-full min-w-[1.2rem] text-center">
                {sortedReplies.length}
              </span>
            )}
          </TabsTrigger>
        </TabsList>

        <div className="text-sm font-medium">
          {/* {activeTab === "replied" && "Replied Messages"}
          {activeTab === "pending" && "Pending Messages"}
          {activeTab === "skipped" && "Skipped Messages"}
          {activeTab === "spam" && "Spam Messages"} */}
        </div>
      </div>

      {/* Replied Tab */}
      <TabsContent value="replied" className="mt-0">
        <RepliedTab
          isLoading={isRepliedMessagesLoading}
          replies={sortedReplies}
          personaData={personaData}
          onRefresh={onRefresh}
          isRefreshing={isRefreshing}
        />
      </TabsContent>

      {/* Pending Tab */}
      <TabsContent value="pending" className="mt-0">
        <PendingTab
          isLoading={isPendingRepliesLoading}
          replies={sortedReplies}
          personaData={personaData}
          automatedReplies={automatedReplies}
          onApprove={handleApprove}
          onReject={handleReject}
          onRefresh={onRefresh}
          isRefreshing={isRefreshing}
        />
      </TabsContent>

      {/* Skipped Tab */}
      <TabsContent value="skipped" className="mt-0">
        <SkippedTab
          isLoading={isSkippedRepliesLoading}
          replies={sortedReplies}
          personaId={personaId}
          onRefresh={onRefresh}
          isRefreshing={isRefreshing}
        />
      </TabsContent>

      {/* Spam Tab */}
      <TabsContent value="spam" className="mt-0">
        <SpamTab
          isLoading={isSkippedRepliesLoading}
          replies={sortedReplies}
          personaId={personaId}
          onRefresh={onRefresh}
          isRefreshing={isRefreshing}
        />
      </TabsContent>
    </Tabs>
  );
};

export default ReplyTabs;
